import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { Policy, Section, SubSection } from 'views/App/Portal/Policy/_types/policy';
import manualHelper from 'views/App/Portal/Policy/_helpers/manualHelper';
import SearchBarWithPersistentResults from './_components/SearchBarWithPersistentResults';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import IssuedManualsSearchPolicy from './_components/IssuedManualsSearchPolicy';
import IssuedManualsSearchPreface from './_components/IssuedManualsSearchPreface';
import DraftManualsSearchPolicy from './_components/DraftManualsSearchPolicy';
import DraftManualsSearchPreface from './_components/DraftManualsSearchPreface';
import { useSearchContext } from './context';
import { Manual, ManualPreface } from 'views/App/Portal/Policy/_types/manuals/draft';
import { DraftManual, DraftPolicy, DraftPreface } from 'views/App/Portal/Policy/_types/manuals/draft';
import Loading from '../../Loading';

const IssuedManualsSearchPage: React.FC = () => {
  const { setCurrentModule, setMainNavCollapsed } = useContext(AppContext);
  const { isContentLoading, setIsContentLoading, searchType } = useSearchContext();

  const [searchParams] = useSearchParams();
  const content = searchParams.get('content') || '';
  useEffect(() => {
    setMainNavCollapsed(true);
    return () => {
      setMainNavCollapsed(false);
    };
  }, [setCurrentModule, setMainNavCollapsed]);

  const [manual, setManual] = useState<Manual | DraftManual | null>();
  const [policy, setPolicy] = useState<Policy | DraftPolicy | null>();
  const [preface, setPreface] = useState<ManualPreface | DraftPreface | null>();
  const [section, setSection] = useState<Section | null>();
  const [subSection, setSubSection] = useState<SubSection | null>();

  useEffect(() => {
    setIsContentLoading(true);
    // Reset all state values before fetching new data
    setManual(null);
    setPolicy(null);
    setPreface(null);
    setSection(null);
    setSubSection(null);

    manualHelper
      .getNodeByContentUri(content)
      .then((result) => {
        if (result?.manual) {
          setManual(result.manual);
        }
        if (result?.policy) {
          setPolicy(result.policy);
        }
        if (result?.preface) {
          setPreface(result.preface);
        }
        if (result?.section) {
          setSection(result.section);
        }
        if (result?.subSection) {
          setSubSection(result.subSection);
        }
      })
      .finally(() => {
        setIsContentLoading(false);
      });
  }, [content]);

  // Determine if we're dealing with draft or issued manual based on search type
  const isDraftManual = searchType === 'draft';

  return (
    <TakeoverLayout
      pageTitle={t('draftManuals.searchManuals.title')}
      backUrl={'/admin/dashboard'}
      leftDrawer={<SearchBarWithPersistentResults />}
      contentHeaderRight={
        <Stack
          direction='row'
          spacing={4}
        >
          <Box />
        </Stack>
      }
    >
      {manual && (policy || preface) && !isContentLoading ? (
        policy ? (
          isDraftManual ? (
            <DraftManualsSearchPolicy
              manualId={manual.id}
              policy={policy as DraftPolicy}
              manual={manual as DraftManual}
              scrollId={subSection?.id || section?.id}
            />
          ) : (
            <IssuedManualsSearchPolicy
              manualId={manual.id}
              policy={policy as Policy}
              scrollId={subSection?.id || section?.id}
            />
          )
        ) : isDraftManual ? (
          <DraftManualsSearchPreface
            manualId={manual.id}
            preface={preface as DraftPreface}
          />
        ) : (
          <IssuedManualsSearchPreface
            manualId={manual.id}
            preface={preface as ManualPreface}
          />
        )
      ) : (
        <>
          {isContentLoading ? (
            <Loading />
          ) : (
            <Typography
              variant='body1'
              color='text.secondary'
            >
              {t('draftManuals.searchManuals.placeholder')}
            </Typography>
          )}
        </>
      )}
    </TakeoverLayout>
  );
};

export default IssuedManualsSearchPage;
